import { useState } from 'react'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { List, ChevronDown, ChevronRight, Sparkles } from 'lucide-react'
import { useNewsStore } from '../stores/newsStore'
import { cn } from '@/lib/utils'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SearchSettings Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function SearchSettings() {
  const [isCollapsed, setIsCollapsed] = useState(true)
  const { settings, updateSettings } = useNewsStore()

  return (
    <div className="space-y-2">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex min-h-11 w-full items-center justify-between text-base font-semibold transition-colors hover:text-primary lg:min-h-0"
      >
        <span>검색 설정</span>
        {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {/* Collapsible Content */}
      <div
        className={cn(
          'grid transition-all duration-200',
          isCollapsed ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'
        )}
      >
        <div className="overflow-hidden">
          <div className="space-y-3 pt-2">
            {/* Items per page */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <List className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="items-per-page" className="font-normal">
                  페이지당 항목 수
                </Label>
              </div>
              <select
                id="items-per-page"
                value={settings.display}
                onChange={(e) => updateSettings({ display: Number(e.target.value) })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="12">12개</option>
                <option value="24">24개</option>
                <option value="36">36개</option>
                <option value="48">48개</option>
              </select>
            </div>

            {/* Sort method */}
            <div className="space-y-2">
              <Label htmlFor="sort-method" className="text-sm font-normal">
                정렬 방식
              </Label>
              <select
                id="sort-method"
                value={settings.sort}
                onChange={(e) => updateSettings({ sort: e.target.value as 'sim' | 'date' })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="sim">관련도순</option>
                <option value="date">최신순</option>
              </select>
            </div>

            {/* AI Summary Toggle */}
            <div className="space-y-2 rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="enable-ai-summary"
                  checked={settings.enableAISummary}
                  onCheckedChange={(checked) =>
                    updateSettings({ enableAISummary: checked as boolean })
                  }
                  className="mt-0.5"
                />
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor="enable-ai-summary"
                    className="flex items-center gap-1.5 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    <Sparkles className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    AI 요약 활성화
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    검색 결과를 AI가 자동으로 요약합니다
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">설정은 자동으로 저장됩니다.</p>
          </div>
        </div>
      </div>
    </div>
  )
}
